'use client'

import type { ActionResult } from '@mentora/app-shared/i18n/action-result'
import type { AuthorityRole } from '@mentora/shared-components'
import { useActionToast } from '@mentora/app-shared/i18n/use-action-toast'
import { emailField, orgNameField, urlField } from '@mentora/app-shared/schemas/fields'
import { AUTHORITY_ROLES, parseRole } from '@mentora/shared-components'
import { Avatar, AvatarFallback, AvatarImage, Badge, Button, Card, CardContent, CardHeader, CardTitle, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle, Switch } from '@mentora/ui-components'
import { Mail, Pencil, Trash2, UserPlus } from 'lucide-react'
import { useFormatter, useTranslations } from 'next-intl'
import { useMemo, useState, useTransition } from 'react'

interface OrganizationProfile {
  id: string
  name: string
  slug: string
  logo: string | null
  createdAt: string
}

interface MemberItem {
  id: string
  userId: string
  role: string
  createdAt: string
  userName: string
  userEmail: string
  userImage: string | null
}

interface InvitationItem {
  id: string
  email: string
  role: string | null
  status: string
  createdAt: string
  expiresAt: string
  inviterName: string | null
}

interface Stats {
  members: number
  pendingInvites: number
  courses: number
  publishedCourses: number
  lessons: number
}

export interface OrganizationPanelActions {
  cancelInvitation: (input: {
    invitationId: string
  }) => Promise<ActionResult>
  inviteOrgMember: (input: {
    email: string
    role: AuthorityRole
    isInstructor: boolean
  }) => Promise<ActionResult>
  removeMember: (input: {
    memberId: string
  }) => Promise<ActionResult>
  updateMemberRole: (input: {
    memberId: string
    role: AuthorityRole
    isInstructor: boolean
  }) => Promise<ActionResult>
  updateOrgProfile: (input: {
    name: string
    logo?: string
  }) => Promise<ActionResult>
}

export interface OrganizationPanelProps {
  organization: OrganizationProfile
  members: MemberItem[]
  invitations: InvitationItem[]
  stats: Stats
  currentUserId: string
  currentMemberRole: string
  /** The storefront's own server actions — the one thing the two copies of this screen never shared. */
  actions: OrganizationPanelActions
}

function initialsFor(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() ?? '')
    .join('') || '?'
}

export function OrganizationPanel({
  organization,
  members,
  invitations,
  stats,
  currentUserId,
  currentMemberRole,
  actions,
}: OrganizationPanelProps) {
  const format = useFormatter()
  const t = useTranslations('organization')
  const tRoles = useTranslations('organizations.roles')
  const tCommon = useTranslations('common')
  const { showError, t: tt } = useActionToast()
  const [isPending, startTransition] = useTransition()
  const [feedback, setFeedback] = useState<{ kind: 'error' | 'success', text: string } | null>(null)

  const [editOpen, setEditOpen] = useState(false)
  const [profileForm, setProfileForm] = useState({
    name: organization.name,
    logo: organization.logo ?? '',
  })

  const [inviteOpen, setInviteOpen] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<AuthorityRole>('member')
  const [inviteIsInstructor, setInviteIsInstructor] = useState(false)

  const [removeTarget, setRemoveTarget] = useState<MemberItem | null>(null)

  const ownerCount = useMemo(
    () => members.filter(m => parseRole(m.role).authority === 'owner').length,
    [members],
  )

  const currentMemberParsed = useMemo(
    () => parseRole(currentMemberRole),
    [currentMemberRole],
  )

  function resetFeedback() {
    setFeedback(null)
  }

  function openEdit() {
    resetFeedback()
    setProfileForm({
      name: organization.name,
      logo: organization.logo ?? '',
    })
    setEditOpen(true)
  }

  function openInvite() {
    resetFeedback()
    setInviteEmail('')
    setInviteRole('member')
    setInviteIsInstructor(false)
    setInviteOpen(true)
  }

  function runAction(
    action: () => Promise<ActionResult>,
    successMessage: string,
    onDone?: () => void,
  ) {
    startTransition(async () => {
      resetFeedback()
      const result = await action()
      if (!result.ok) {
        showError(result)
        setFeedback({ kind: 'error', text: tt(result.errorKey, result.errorValues) })
        return
      }
      setFeedback({ kind: 'success', text: successMessage })
      onDone?.()
    })
  }

  // Mirror the server action's validation client-side using the same shared
  // field schemas + error keys, so feedback is instant and identical.
  function saveProfile() {
    if (!orgNameField.safeParse(profileForm.name).success) {
      setFeedback({ kind: 'error', text: tt('organization.errors.nameLength') })
      return
    }
    if (profileForm.logo.trim() && !urlField.safeParse(profileForm.logo).success) {
      setFeedback({ kind: 'error', text: tt('organization.errors.logoInvalid') })
      return
    }
    runAction(
      () => actions.updateOrgProfile(profileForm),
      t('toasts.updated'),
      () => setEditOpen(false),
    )
  }

  function submitInvite() {
    if (!emailField.safeParse(inviteEmail).success) {
      setFeedback({ kind: 'error', text: tt('organization.errors.emailInvalid') })
      return
    }
    runAction(
      () => actions.inviteOrgMember({
        email: inviteEmail,
        role: inviteRole,
        isInstructor: inviteIsInstructor,
      }),
      t('toasts.invitationSent'),
      () => setInviteOpen(false),
    )
  }

  function handleAuthorityChange(memberItem: MemberItem, role: AuthorityRole) {
    const { isInstructor } = parseRole(memberItem.role)
    runAction(
      () => actions.updateMemberRole({ memberId: memberItem.id, role, isInstructor }),
      t('toasts.roleUpdated', { name: memberItem.userName }),
    )
  }

  function handleInstructorToggle(memberItem: MemberItem, next: boolean) {
    const { authority } = parseRole(memberItem.role)
    runAction(
      () => actions.updateMemberRole({
        memberId: memberItem.id,
        role: authority,
        isInstructor: next,
      }),
      t('toasts.roleUpdated', { name: memberItem.userName }),
    )
  }

  function confirmRemove() {
    if (!removeTarget)
      return
    const target = removeTarget
    runAction(
      () => actions.removeMember({ memberId: target.id }),
      t('toasts.memberRemoved', { name: target.userName }),
      () => setRemoveTarget(null),
    )
  }

  function renderInvitationRoleBadges(role: string | null) {
    if (!role) {
      return <Badge variant="secondary" className="mr-2">{tRoles('member')}</Badge>
    }
    const { authority, isInstructor } = parseRole(role)
    return (
      <>
        <Badge variant="secondary" className="mr-2">{tRoles(authority)}</Badge>
        {isInstructor && (
          <Badge variant="outline" className="mr-2">{tRoles('instructor')}</Badge>
        )}
      </>
    )
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 py-8 sm:px-8">
        <div className="rounded-2xl border border-border/60 bg-gradient-to-br from-background to-muted/30 p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-4">
              <Avatar className="size-14">
                {organization.logo
                  ? <AvatarImage src={organization.logo} alt={organization.name} />
                  : null}
                <AvatarFallback>{initialsFor(organization.name)}</AvatarFallback>
              </Avatar>
              <div className="space-y-1">
                <h1 className="font-(--font-display) text-3xl tracking-tight text-foreground">
                  {organization.name}
                </h1>
                <p className="text-sm text-muted-foreground">
                  /
                  {organization.slug}
                  {' · '}
                  {t('youAre', { role: tRoles(currentMemberParsed.authority) })}
                  {currentMemberParsed.isInstructor && (
                    <>
                      {' · '}
                      {tRoles('instructor')}
                    </>
                  )}
                </p>
              </div>
            </div>
            <Button variant="outline" onClick={openEdit}>
              <Pencil className="size-4" />
              {t('editProfile')}
            </Button>
          </div>
          {feedback && (
            <p className={`mt-4 text-sm ${feedback.kind === 'error' ? 'text-destructive' : 'text-success'}`}>
              {feedback.text}
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
          <StatCard label={t('stats.members')} value={stats.members} />
          <StatCard label={t('stats.pendingInvites')} value={stats.pendingInvites} />
          <StatCard label={t('stats.courses')} value={stats.courses} />
          <StatCard label={t('stats.published')} value={stats.publishedCourses} />
          <StatCard label={t('stats.lessons')} value={stats.lessons} />
        </div>

        <Card className="border-border/50">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-xl">{t('members')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {members.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('noMembers')}</p>
            )}
            {members.map((m) => {
              const { authority, isInstructor } = parseRole(m.role)
              const isSelf = m.userId === currentUserId
              const isLastOwner = authority === 'owner' && ownerCount <= 1
              const removeDisabled = isPending || isSelf || isLastOwner
              const roleSelectDisabled = isPending || isSelf
              return (
                <div
                  key={m.id}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-md border border-border/60 p-3"
                >
                  <div className="flex items-center gap-3">
                    <Avatar>
                      {m.userImage ? <AvatarImage src={m.userImage} alt={m.userName} /> : null}
                      <AvatarFallback>{initialsFor(m.userName)}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {m.userName}
                        {isSelf && (
                          <span className="ml-2 text-xs text-muted-foreground">{t('you')}</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {m.userEmail}
                        {' · '}
                        {t('joinedOn', { date: format.dateTime(new Date(m.createdAt), { dateStyle: 'medium' }) })}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Select
                      value={authority}
                      onValueChange={value => handleAuthorityChange(m, value as AuthorityRole)}
                      disabled={roleSelectDisabled}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AUTHORITY_ROLES.map(option => (
                          <SelectItem key={option} value={option}>
                            {tRoles(option)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Switch
                        checked={isInstructor}
                        onCheckedChange={next => handleInstructorToggle(m, next)}
                        disabled={isPending}
                      />
                      {tRoles('instructor')}
                    </label>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={removeDisabled}
                      onClick={() => {
                        resetFeedback()
                        setRemoveTarget(m)
                      }}
                    >
                      <Trash2 className="size-4" />
                      {t('remove')}
                    </Button>
                  </div>
                </div>
              )
            })}
          </CardContent>
        </Card>

        <Card className="border-border/50">
          <CardHeader className="flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-xl">{t('invitations')}</CardTitle>
            <Button variant="outline" size="sm" onClick={openInvite}>
              <UserPlus className="size-4" />
              {t('inviteMember')}
            </Button>
          </CardHeader>
          <CardContent className="space-y-2">
            {invitations.length === 0 && (
              <p className="text-sm text-muted-foreground">{t('noPendingInvitations')}</p>
            )}
            {invitations.map(invite => (
              <div
                key={invite.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 p-3"
              >
                <div className="flex items-center gap-3">
                  <Mail className="size-4 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{invite.email}</p>
                    <p className="text-xs text-muted-foreground">
                      {renderInvitationRoleBadges(invite.role)}
                      {invite.inviterName
                        ? t('invitedBy', { name: invite.inviterName })
                        : t('invitedBy', { name: t('system') })}
                      {' · '}
                      {t('expiresOn', { date: format.dateTime(new Date(invite.expiresAt), { dateStyle: 'medium' }) })}
                    </p>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  disabled={isPending}
                  onClick={() =>
                    runAction(
                      () => actions.cancelInvitation({ invitationId: invite.id }),
                      t('toasts.invitationCanceled'),
                    )}
                >
                  {tCommon('cancel')}
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>

      <Sheet open={editOpen} onOpenChange={setEditOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{t('editSheet.title')}</SheetTitle>
            <SheetDescription>{t('editSheet.description')}</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 px-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('fields.name')}</label>
              <Input
                placeholder={t('fields.namePlaceholder')}
                value={profileForm.name}
                onChange={e => setProfileForm(prev => ({ ...prev, name: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">
                {t('fields.logoUrl')}
                {' '}
                <span className="text-muted-foreground font-normal">{t('fields.logoOptional')}</span>
              </label>
              <Input
                placeholder={t('fields.logoPlaceholder')}
                value={profileForm.logo}
                onChange={e => setProfileForm(prev => ({ ...prev, logo: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('fields.slug')}</label>
              <Input value={organization.slug} disabled />
            </div>
          </div>
          <SheetFooter>
            <Button
              disabled={isPending}
              onClick={saveProfile}
            >
              {isPending ? tCommon('saving') : tCommon('saveChanges')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Sheet open={inviteOpen} onOpenChange={setInviteOpen}>
        <SheetContent className="w-full sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{t('inviteSheet.title')}</SheetTitle>
            <SheetDescription>{t('inviteSheet.description')}</SheetDescription>
          </SheetHeader>
          <div className="space-y-4 px-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('fields.email')}</label>
              <Input
                placeholder={t('fields.emailPlaceholder')}
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-foreground">{t('fields.role')}</label>
              <Select
                value={inviteRole}
                onValueChange={value => setInviteRole(value as AuthorityRole)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUTHORITY_ROLES.map(option => (
                    <SelectItem key={option} value={option}>
                      {tRoles(option)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center justify-between gap-2 rounded-md border border-border/60 p-3">
              <span className="text-sm font-medium text-foreground">
                {tRoles('instructor')}
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  {t('fields.isInstructorHint')}
                </span>
              </span>
              <Switch
                checked={inviteIsInstructor}
                onCheckedChange={setInviteIsInstructor}
              />
            </label>
          </div>
          <SheetFooter>
            <Button
              disabled={isPending}
              onClick={submitInvite}
            >
              {isPending ? tt('organizations.buttons.sending') : tt('organizations.buttons.send')}
            </Button>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <Dialog open={!!removeTarget} onOpenChange={open => !open && setRemoveTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('removeDialog.title')}</DialogTitle>
            <DialogDescription>
              {t('removeDialog.description', { name: removeTarget?.userName ?? '' })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRemoveTarget(null)}
              disabled={isPending}
            >
              {tCommon('cancel')}
            </Button>
            <Button
              className="bg-destructive/90 text-overlay-foreground hover:bg-destructive focus-visible:ring-destructive"
              onClick={confirmRemove}
              disabled={isPending}
            >
              {isPending ? t('removing') : t('remove')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

function StatCard({ label, value }: { label: string, value: number }) {
  return (
    <div className="rounded-lg border border-border/60 bg-card/40 p-4">
      <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-foreground">{value}</p>
    </div>
  )
}
